import type { ReactNode } from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router";
import dayjs from "dayjs";

import { isErrorWithStatus } from "../../api/queryClient";
import { logError } from "../../api/logError";

type StatusHandler = (info: { error: unknown }) => React.JSX.Element | null;
type UnexpectedErrorHandler = (error: unknown) => React.JSX.Element | null;
export type AuthErrorHandler = {
  handler: (error: unknown) => void;
  isAuthError: (error: unknown) => boolean;
};

const CHUNK_RELOAD_KEY = "chunk-reload-timestamp";
const RELOAD_THROTTLE_MS = 1 * 60 * 1000; /* 1 minute */

export const ErrorBoundary = ({
  children,
  statusHandlers,
  unexpectedErrorHandler,
  authErrorHandler,
}: {
  children: ReactNode;
  statusHandlers: Record<number, StatusHandler>;
  unexpectedErrorHandler: UnexpectedErrorHandler;
  authErrorHandler?: AuthErrorHandler;
}) => {
  const { pathname, search } = useLocation();
  return (
    <ReactErrorBoundary
      resetKeys={[pathname, search]}
      onError={error => {
        logError(error, { loggedFrom: "ErrorBoundary" });

        if (authErrorHandler?.isAuthError(error)) return authErrorHandler.handler(error);

        if (isChunkError(error)) {
          /* Prevent infinite reload loops - only reload if enough time has passed since last reload */
          const lastReloadTime = sessionStorage.getItem(CHUNK_RELOAD_KEY);
          const now = dayjs();
          const shouldReload = !lastReloadTime || now.diff(dayjs(lastReloadTime), "millisecond") > RELOAD_THROTTLE_MS;

          if (shouldReload) {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, now.toISOString());
            location.reload();
          }
        }
      }}
      fallbackRender={({ error }) => {
        if (authErrorHandler?.isAuthError(error)) return null;

        if (isChunkError(error)) return null;

        if (isErrorWithStatus(error) && statusHandlers?.[error.status]) {
          return statusHandlers[error.status]({ error });
        }
        return unexpectedErrorHandler(error);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
};

/**
 * Checks if an error is a chunk loading error.
 * These errors occur when the browser tries to lazy load a chunk that no longer exists,
 * typically after a new deployment when users have stale cached chunk references.
 */
const isChunkError = (error: unknown) => {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("dynamically imported module") || error.message.includes("not a valid JavaScript MIME type")
  );
};
